import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentDuplicateIcon, 
  ArrowDownTrayIcon,
  EyeIcon,
  ShareIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../config';
import { downloadBadgeAsImage, copyBadgeUrl } from '../utils/downloadUtils';
import { downloadBadgeFromPreview } from '../utils/imageDownload';
import { downloadBadgeSimple } from '../utils/simpleDownload';

interface Session {
  id: string;
  title: string;
  description: string;
  type: string;
  speakers: { id: string; name: string; display_order: number }[];
  startTime: string;
  endTime: string;
  room: string;
  status: string;
}

interface BadgeConfig {
  theme: string;
  size: string;
  format: string;
  showSpeakers: boolean;
  showTime: boolean;
  showRoom: boolean;
  watermark: boolean;
  customTitle: string;
  customText: string;
}

const themes = [
  { id: 'default', name: 'Conference Blue', preview: 'linear-gradient(135deg, #00529B 0%, #003D74 100%)' },
  { id: 'gradient', name: 'Gradient', preview: 'linear-gradient(135deg, #00529B 0%, #E31D3C 100%)' },
  { id: 'minimal', name: 'Minimal', preview: '#ffffff' },
  { id: 'dark', name: 'Dark', preview: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)' },
  { id: 'keynote', name: 'Keynote Gold', preview: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }
];

const sizes = [
  { id: 'small', name: 'Small (320x192)', dimensions: '320×192px' },
  { id: 'medium', name: 'Medium (384x256)', dimensions: '384×256px' },
  { id: 'large', name: 'Large (600x320)', dimensions: '600×320px' }
];

const formats = [
  { id: 'card', name: 'Card', description: 'Standard card format' },
  { id: 'banner', name: 'Banner', description: 'Wide banner format' },
  { id: 'square', name: 'Square', description: 'Square format for Instagram' },
  { id: 'story', name: 'Story', description: 'Vertical story format' }
];

const SessionBadgeGenerator: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<string>('');
  const [config, setConfig] = useState<BadgeConfig>({
    theme: 'default',
    size: 'large',
    format: 'card',
    showSpeakers: true,
    showTime: true,
    showRoom: false, // Changed default to false
    watermark: true,
    customTitle: '',
    customText: ''
  });
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        console.log('Fetching sessions from /public/sessions...');
        const response = await fetch(`${API_BASE_URL}/public/sessions`);
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Sessions data received:', data);
        console.log('Number of sessions:', data.sessions?.length || 0);
        
        setSessions(data.sessions || []);
        if (data.sessions && data.sessions.length > 0) {
          setSelectedSession(data.sessions[0].id);
          console.log('Selected first session:', data.sessions[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      const params = new URLSearchParams();
      params.set('theme', config.theme);
      params.set('size', config.size);
      params.set('format', config.format);
      params.set('speakers', config.showSpeakers.toString());
      params.set('time', config.showTime.toString());
      params.set('room', config.showRoom.toString());
      params.set('watermark', config.watermark.toString());
      
      if (config.customTitle) params.set('title', config.customTitle);
      if (config.customText) params.set('text', config.customText);

      setPreviewUrl(`/badge/${selectedSession}?${params.toString()}`);
    }
  }, [selectedSession, config]);

  const generateShareableUrl = () => {
    return `${window.location.origin}${previewUrl}`;
  };

  const handleCopyUrl = () => {
    const success = copyBadgeUrl(generateShareableUrl());
    if (success) {
      // You could add a toast notification here
      console.log('URL copied to clipboard');
    }
  };

  const handleDownload = async () => {
    const session = sessions.find(s => s.id === selectedSession);
    const filename = session ? `${session.title.replace(/[^a-zA-Z0-9]/g, '-')}-badge.png` : 'session-badge.png';
    
    try {
      await downloadBadgeSimple(generateShareableUrl(), filename);
    } catch (error) {
      console.error('Simple download failed, trying iframe method:', error);
      try {
        await downloadBadgeFromPreview(generateShareableUrl(), filename);
      } catch (error2) {
        console.error('Iframe download failed, falling back to manual method:', error2);
        downloadBadgeAsImage(generateShareableUrl(), filename);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Session Badge Generator
          </h1>
          <p className="text-gray-600">
            Create beautiful social media badges for conference sessions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Session Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Cog6ToothIcon className="w-5 h-5 mr-2" />
                Session Selection ({sessions.length} sessions loaded)
              </h3>
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
              >
                <option value="">Select a session...</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Theme Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Theme</h3>
              <div className="grid grid-cols-1 gap-3">
                {themes.map((theme) => (
                  <label
                    key={theme.id}
                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      config.theme === theme.id
                        ? 'border-[#00529B] bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="theme"
                      value={theme.id}
                      checked={config.theme === theme.id}
                      onChange={(e) => setConfig({ ...config, theme: e.target.value })}
                      className="sr-only"
                    />
                    <div
                      className="w-6 h-6 rounded-full mr-3 border border-gray-300"
                      style={{ background: theme.preview }}
                    />
                    <span className="font-medium">{theme.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Size & Format */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Size & Format</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                <select
                  value={config.size}
                  onChange={(e) => setConfig({ ...config, size: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                >
                  {sizes.map((size) => (
                    <option key={size.id} value={size.id}>
                      {size.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
                <select
                  value={config.format}
                  onChange={(e) => setConfig({ ...config, format: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                >
                  {formats.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.name} - {format.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Display Options */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Display Options</h3>
              <div className="space-y-3">
                {[
                  { key: 'showSpeakers', label: 'Show Speakers' },
                  { key: 'showTime', label: 'Show Time & Date' },
                  { key: 'showRoom', label: 'Show Room' },
                  { key: 'watermark', label: 'Show Conference Branding' }
                ].map((option) => (
                  <label key={option.key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config[option.key as keyof BadgeConfig] as boolean}
                      onChange={(e) => setConfig({ ...config, [option.key]: e.target.checked })}
                      className="rounded border-gray-300 text-[#00529B] focus:ring-[#00529B]"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Text */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Text</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Title (optional)
                  </label>
                  <input
                    type="text"
                    value={config.customTitle}
                    onChange={(e) => setConfig({ ...config, customTitle: e.target.value })}
                    placeholder="Override session title..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Description (optional)
                  </label>
                  <textarea
                    value={config.customText}
                    onChange={(e) => setConfig({ ...config, customText: e.target.value })}
                    placeholder="Override session description..."
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview & Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Preview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              {selectedSession && previewUrl ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <iframe
                    src={previewUrl}
                    className="w-full h-96 border-0"
                    title="Badge Preview"
                  />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg h-96 flex items-center justify-center text-gray-500">
                  Select a session to see preview
                </div>
              )}
            </div>

            {/* Actions */}
            {selectedSession && previewUrl && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Share & Download</h3>
                
                <div className="space-y-4">
                  {/* Shareable URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shareable URL
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={generateShareableUrl()}
                        readOnly
                        className="flex-1 p-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
                      />
                      <button
                        onClick={handleCopyUrl}
                        className="px-4 py-2 bg-[#00529B] text-white rounded-r-lg hover:bg-[#003D74] transition-colors flex items-center"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={previewUrl}
                      target="_blank"
                      className="inline-flex items-center px-4 py-2 bg-[#00529B] text-white rounded-lg hover:bg-[#003D74] transition-colors"
                    >
                      <EyeIcon className="w-4 h-4 mr-2" />
                      View Full Size
                    </Link>
                    
                    <button
                      onClick={handleDownload}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download
                    </button>
                    
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: 'Session Badge',
                            url: generateShareableUrl()
                          });
                        } else {
                          handleCopyUrl();
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ShareIcon className="w-4 h-4 mr-2" />
                      Share
                    </button>
                  </div>

                  {/* Usage Examples */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Usage Examples:</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Share on social media to promote sessions</div>
                      <div>• Embed in newsletters and blog posts</div>
                      <div>• Use in conference materials and presentations</div>
                      <div>• Create custom promotional graphics</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionBadgeGenerator;
